<script setup lang="ts">
/**
 * CoreSliderField
 *
 * Single-value slider field built from:
 * - `CoreField` wrapper (label/description/errors/a11y wiring)
 * - `<input type="range">` control with stable variant/density styling
 *
 * Validation behavior mirrors other core fields.
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, toRef } from 'vue'
import CoreField from '../CoreField.vue'
import type { CoreErrorMessages, CoreFieldDensity, CoreFieldVariant, CoreRule } from '../types'
import { coreFormKey } from '../keys'
import { getCoreSliderClasses } from '../controlClasses'
import { normalizeErrorMessages, useRulesValidation } from '../useRulesValidation'
import { useStableId } from '../useStableId'

const props = withDefaults(
  defineProps<{
    modelValue: number
    min?: number
    max?: number
    step?: number
    showValue?: boolean
    formatValue?: (value: number) => string
    label?: string
    description?: string
    errorMessages?: CoreErrorMessages
    rules?: CoreRule<number>[]
    variant?: CoreFieldVariant
    density?: CoreFieldDensity
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    hideDetails?: 'auto' | 'always' | 'never'
  }>(),
  {
    min: 0,
    max: 100,
    step: 1,
    showValue: false,
    formatValue: undefined,
    label: undefined,
    description: undefined,
    errorMessages: undefined,
    rules: undefined,
    variant: 'outlined',
    density: 'default',
    disabled: false,
    readonly: false,
    required: false,
    name: undefined,
    id: undefined,
    hideDetails: 'auto',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const form = inject(coreFormKey, null)
const touched = ref(false)

const stableId = useStableId({
  id: toRef(props, 'id'),
  name: toRef(props, 'name'),
  prefix: 'core-field',
})

const valueRef = computed({
  get: () => props.modelValue,
  set: (next) => emit('update:modelValue', next),
})

const { ruleErrors, validateRules, resetRules } = useRulesValidation({
  value: valueRef,
  rules: toRef(props, 'rules'),
})

const externalErrors = computed(() => normalizeErrorMessages(props.errorMessages))
const hasExternalErrors = computed(() => externalErrors.value.length > 0)
const canShowErrors = computed(() => touched.value || form?.submitted.value)

const activeErrors = computed<string[]>(() => {
  if (!canShowErrors.value) return []
  if (hasExternalErrors.value) return externalErrors.value
  return ruleErrors.value
})

const invalid = computed(() => activeErrors.value.length > 0)
const controlClass = computed(() =>
  getCoreSliderClasses({ variant: props.variant, density: props.density, invalid: invalid.value }),
)
const sliderStateClass = computed(() => ({
  'core-slider--disabled': props.disabled,
  'core-slider--readonly': props.readonly,
}))

const validateCurrent = async () => {
  touched.value = true
  if (props.disabled) {
    resetRules()
    return []
  }
  if (hasExternalErrors.value) return externalErrors.value
  return await validateRules()
}

const resetValidation = () => {
  touched.value = false
  resetRules()
}

let unregister: (() => void) | null = null
onMounted(() => {
  if (!form) return
  const key = props.name || stableId.value
  unregister = form.registerField({
    key,
    validate: validateCurrent,
    resetValidation,
  })
})

onBeforeUnmount(() => unregister?.())

const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  if (props.readonly) {
    target.value = String(props.modelValue)
    return
  }
  const next = Number(target.value)
  if (Number.isNaN(next)) return
  valueRef.value = next

  if (canShowErrors.value && !hasExternalErrors.value) {
    await validateRules()
  }
}

const onBlur = async () => {
  await validateCurrent()
}

const shouldShowValue = computed(() => props.showValue || typeof props.formatValue === 'function')
const displayValue = computed(() => {
  if (typeof props.formatValue === 'function') return props.formatValue(props.modelValue)
  return String(props.modelValue)
})
</script>

<template>
  <CoreField
    :id="stableId"
    :name="name"
    :label="label"
    :description="description"
    :error-messages="activeErrors"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
    :variant="variant"
    :density="density"
    :hide-details="hideDetails"
  >
    <template #default="{ id: controlId, describedBy, invalid: slotInvalid, disabled: slotDisabled, readonly: slotReadonly, required: slotRequired }">
      <div class="flex items-center gap-3">
        <input
          :id="controlId"
          type="range"
          :name="name"
          :value="modelValue"
          :min="min"
          :max="max"
          :step="step"
          :disabled="slotDisabled"
          :required="slotRequired"
          :aria-invalid="slotInvalid ? 'true' : undefined"
          :aria-describedby="describedBy"
          :aria-readonly="slotReadonly ? 'true' : undefined"
          :class="[controlClass, sliderStateClass]"
          @input="onInput"
          @blur="onBlur"
        />
        <span
          v-if="shouldShowValue"
          class="text-xs text-on-surface-variant min-w-[2.5rem] text-right"
        >
          {{ displayValue }}
        </span>
      </div>
    </template>
  </CoreField>
</template>

<style scoped>
.core-slider {
  --core-slider-track-height: 0.375rem;
  --core-slider-thumb-size: 1rem;
  --core-slider-track-bg: var(--theme-field-surface);
  --core-slider-track-border: var(--theme-field-border);
  --core-slider-thumb-bg: var(--theme-primary);
  --core-slider-thumb-border: var(--theme-surface-solid);
  --core-slider-progress-bg: var(--theme-primary);

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
  height: var(--core-slider-track-height);
  border-radius: 999px;
  background: var(--core-slider-track-bg);
  border: 1px solid var(--core-slider-track-border);
  cursor: pointer;
}

.core-slider:disabled {
  cursor: not-allowed;
}

.core-slider[aria-readonly='true'] {
  cursor: default;
}

.core-slider--disabled {
  --core-slider-track-bg: var(--theme-field-surface-disabled);
  --core-slider-track-border: var(--theme-field-border-disabled);
  --core-slider-thumb-bg: var(--theme-field-disabled-text);
  --core-slider-progress-bg: color-mix(in srgb, var(--theme-primary) 45%, transparent);
}

.core-slider--readonly {
  --core-slider-track-bg: var(--theme-field-surface-readonly);
  --core-slider-track-border: var(--theme-field-border-readonly);
  --core-slider-thumb-bg: color-mix(in srgb, var(--theme-primary) 82%, var(--theme-on-surface) 18%);
  --core-slider-progress-bg: color-mix(in srgb, var(--theme-primary) 70%, transparent);
}

.core-slider--compact {
  --core-slider-track-height: 0.3125rem;
  --core-slider-thumb-size: 0.875rem;
}

.core-slider--comfortable {
  --core-slider-track-height: 0.5rem;
  --core-slider-thumb-size: 1.25rem;
}

.core-slider--outlined {
  --core-slider-track-bg: var(--theme-field-surface);
  --core-slider-track-border: var(--theme-field-border);
}

.core-slider--filled {
  --core-slider-track-bg: var(--theme-field-surface-filled);
  --core-slider-track-border: var(--theme-field-border-muted);
}

.core-slider--plain {
  --core-slider-track-bg: transparent;
  --core-slider-track-border: var(--theme-field-border-muted);
}

.core-slider--invalid {
  --core-slider-track-border: var(--theme-danger);
  --core-slider-thumb-bg: var(--theme-danger);
  --core-slider-progress-bg: var(--theme-danger);
}

.core-slider::-webkit-slider-runnable-track {
  height: var(--core-slider-track-height);
  border-radius: 999px;
  background: var(--core-slider-track-bg);
  border: 1px solid var(--core-slider-track-border);
}

.core-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: var(--core-slider-thumb-size);
  height: var(--core-slider-thumb-size);
  border-radius: 999px;
  background: var(--core-slider-thumb-bg);
  border: 2px solid var(--core-slider-thumb-border);
  margin-top: calc((var(--core-slider-track-height) - var(--core-slider-thumb-size)) / 2);
}

.core-slider::-moz-range-track {
  height: var(--core-slider-track-height);
  border-radius: 999px;
  background: var(--core-slider-track-bg);
  border: 1px solid var(--core-slider-track-border);
}

.core-slider::-moz-range-progress {
  height: var(--core-slider-track-height);
  border-radius: 999px;
  background: var(--core-slider-progress-bg);
}

.core-slider::-moz-range-thumb {
  width: var(--core-slider-thumb-size);
  height: var(--core-slider-thumb-size);
  border-radius: 999px;
  background: var(--core-slider-thumb-bg);
  border: 2px solid var(--core-slider-thumb-border);
}
</style>
